import type { DeleteUserReq } from '@/apis/user'

export type DeleteReasonSelection = {
  categoryId: string
  subCategoryId: string
  customReason?: string
  suggestion?: string
  followUp?: {
    willing: boolean
    email?: string
  }
}

const PRIMARY_REASON_TYPE_MAP: Record<string, number> = {
  starting_over: 1,
  chat_experience: 2,
  price_gems: 3,
  privacy_concerns: 4,
  personal_reasons: 5,
  other: 6,
}

const PRIMARY_REASON_LABEL_MAP: Record<string, string> = {
  starting_over: 'Starting Over',
  chat_experience: 'Chat Experience',
  price_gems: 'Price & Gems',
  privacy_concerns: 'Privacy Concerns',
  personal_reasons: 'Personal Reasons',
  other: 'Other',
}

const SECONDARY_REASON_TYPE_MAP: Record<string, Record<string, number>> = {
  starting_over: {
    switching_accounts: 1,
    start_fresh: 2,
  },
  chat_experience: {
    repetitive_replies: 1,
    forgets_conversations: 2,
    too_slow: 3,
    no_interesting_stories: 4,
    lack_voice_visual: 5,
  },
  price_gems: {
    too_expensive: 1,
    not_worth_subscription: 2,
  },
  privacy_concerns: {
    data_history_leak: 1,
  },
  personal_reasons: {
    life_got_busy: 1,
    just_trying_out: 2,
  },
}

const SECONDARY_REASON_LABEL_MAP: Record<string, Record<string, string>> = {
  starting_over: {
    switching_accounts: 'Switching accounts (to get gems/rewards)',
    start_fresh: 'Start fresh (to clear all memory)',
  },
  chat_experience: {
    repetitive_replies: 'Repetitive or robotic replies',
    forgets_conversations: 'Forgets our past conversations',
    too_slow: 'Too slow, laggy, or buggy',
    no_interesting_stories: 'No interesting stories/characters',
    lack_voice_visual: 'Lack of voice or visual features',
  },
  price_gems: {
    too_expensive: 'Too expensive / Not enough gems',
    not_worth_subscription: 'Not worth the subscription',
  },
  privacy_concerns: {
    data_history_leak: 'Worried about data/history leak',
  },
  personal_reasons: {
    life_got_busy: 'Life got busy',
    just_trying_out: 'Just trying it out',
  },
}

const trimToUndefined = (value?: string) => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

export const buildDeleteUserReq = (
  reason: DeleteReasonSelection,
  extras: {
    platform: string
    deviceInfo?: string
  }
): DeleteUserReq => {
  const reasonType = PRIMARY_REASON_TYPE_MAP[reason.categoryId]
  const primaryReason = PRIMARY_REASON_LABEL_MAP[reason.categoryId]
  const secondaryReasonType =
    SECONDARY_REASON_TYPE_MAP[reason.categoryId]?.[reason.subCategoryId]
  const secondaryReason =
    SECONDARY_REASON_LABEL_MAP[reason.categoryId]?.[reason.subCategoryId]
  const isOpenToInterview = reason.followUp?.willing

  return {
    reason_type: reasonType,
    primary_reason: primaryReason,
    secondary_reason_type: secondaryReasonType,
    secondary_reason: secondaryReason,
    custom_reason: trimToUndefined(reason.customReason),
    suggestion: trimToUndefined(reason.suggestion),
    platform: extras.platform,
    device_info: trimToUndefined(extras.deviceInfo) || 'Unknown',
    is_open_to_interview: isOpenToInterview,
    interview_email: isOpenToInterview
      ? trimToUndefined(reason.followUp?.email)
      : undefined,
  }
}
